import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Clock, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { articles as staticArticles, media } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp } from "@/components/ui-primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize a DB article payload (body is plain text; `## ` at line start = heading, blank line separates paragraphs)
const normalizeDbArticle = (a) => {
  const langify = (L) => {
    const raw = (L?.body || "").trim();
    // Split into sections by blank lines; each section may start with "## Heading" on its first line
    const sections = raw.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
    const body = sections.map((sec) => {
      const lines = sec.split("\n");
      if (lines[0].startsWith("## ")) {
        return { h: lines[0].slice(3).trim(), p: lines.slice(1).join("\n").trim() };
      }
      return { h: null, p: sec };
    });
    return {
      title: L?.title || "",
      excerpt: L?.excerpt || "",
      body,
    };
  };
  return {
    slug: a.slug,
    date: a.date,
    readTime: a.read_time ?? a.readTime ?? 5,
    category: a.category || { en: "", ru: "", tr: "" },
    image: a.image || "",
    en: langify(a.en),
    ru: langify(a.ru),
    tr: langify(a.tr),
    _source: "db",
  };
};

const NewsArticle = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();

  const staticArticle = staticArticles.find((a) => a.slug === slug);
  const [dbArticle, setDbArticle] = useState(null);
  const [dbLoading, setDbLoading] = useState(true);
  const [dbOtherArticles, setDbOtherArticles] = useState([]);

  useEffect(() => {
    let mounted = true;
    // Always fetch the full list for the "also read" rail
    axios.get(`${API}/articles`).then((res) => {
      if (!mounted) return;
      setDbOtherArticles((res.data.items || []).map(normalizeDbArticle));
    }).catch(() => {});

    // Always try the DB first for the specific article — DB wins on slug match
    // so admin-edited content (including uploaded hero images) is shown over the
    // legacy static version.
    axios.get(`${API}/articles/${slug}`).then((res) => {
      if (!mounted) return;
      setDbArticle(normalizeDbArticle(res.data));
    }).catch(() => {
      if (mounted) setDbArticle(null);
    }).finally(() => {
      if (mounted) setDbLoading(false);
    });

    return () => { mounted = false; };
  }, [slug]);

  if (dbLoading) {
    return (
      <main className="min-h-screen pt-40 flex items-start justify-center">
        <Loader2 className="animate-spin text-navy/40" size={28} />
      </main>
    );
  }

  // DB has priority; fall back to static only if no DB record exists
  const article = dbArticle || staticArticle;
  if (!article) return <Navigate to="/insights" replace />;

  const copy = article[lang] || article.en;
  const hasHero = !!(article.image && article.image.trim());

  // Pool "others" from static + db (excluding current slug)
  const othersPool = [
    ...staticArticles.filter((a) => a.slug !== slug),
    ...dbOtherArticles.filter((a) => a.slug !== slug),
  ];
  const seen = new Set();
  const others = othersPool.filter((a) => {
    if (seen.has(a.slug)) return false;
    seen.add(a.slug);
    return true;
  });

  return (
    <>
      <SEO path={`/insights/${slug}`} title={copy.title} description={copy.excerpt} />

      <section className="relative pt-40 pb-16 overflow-hidden" data-testid="article-header">
        <div className="absolute inset-0">
          {hasHero ? (
            <>
              <img src={article.image} alt="" className="w-full h-full object-cover kenburns" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/50" />
            </>
          ) : (
            <div
              className="w-full h-full"
              style={{
                background:
                  "radial-gradient(circle at 25% 35%, #14294a 0%, #0B1E35 50%, #071322 100%)",
              }}
            >
              <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.07]"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.55) 1px, transparent 1px)",
                  backgroundSize: "26px 26px",
                }}
              />
              <img
                src={media.logo}
                alt=""
                aria-hidden="true"
                className="absolute right-6 bottom-6 sm:right-10 sm:bottom-10 w-32 sm:w-44 opacity-25"
                draggable="false"
              />
            </div>
          )}
        </div>
        <div className="relative px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl">
            <FadeUp>
              <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] uppercase text-brand mb-6">
                <span>{article.category?.[lang] || article.category?.en}</span>
                <span className="w-1 h-1 bg-brand rounded-full" />
                <span>{new Date(article.date).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-GB", {
                  year: "numeric", month: "long", day: "numeric",
                })}</span>
                <span className="w-1 h-1 bg-brand rounded-full" />
                <span className="flex items-center gap-1">
                  <Clock size={10} /> {article.readTime} min
                </span>
              </div>
            </FadeUp>
            <FadeUp delay={0.05}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
                {copy.title}
              </h1>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-2xl">
                {copy.excerpt}
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 sm:px-10 lg:px-16" data-testid="article-body">
        <div className="max-w-3xl">
          {copy.body.map((block, i) => (
            <FadeUp key={i} delay={i * 0.03}>
              <div className="mb-10">
                {block.h && (
                  <h2 className="font-display text-2xl sm:text-3xl text-navy mb-4 leading-tight">
                    {block.h}
                  </h2>
                )}
                <p className="text-navy/70 text-base leading-[1.8] whitespace-pre-line">{block.p}</p>
              </div>
            </FadeUp>
          ))}

          <div className="mt-16 flex items-center justify-between pt-8 border-t border-navy/10">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-brand text-[11px] tracking-[0.25em] uppercase hover:gap-3 transition-all"
            >
              <ArrowLeft size={12} /> {t.common.allArticles}
            </Link>
            <Link
              to="/contact"
              className="text-[11px] tracking-[0.25em] uppercase text-navy/70 hover:text-brand"
            >
              {t.nav.contact} →
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-cream border-t border-navy/10 px-6 sm:px-10 lg:px-16">
        <div className="overline mb-6">{t.common.allArticles}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {others.slice(0, 3).map((o) => (
            <Link
              key={o.slug}
              to={`/insights/${o.slug}`}
              className="group block border border-navy/10 p-6 hover:border-brand/60 transition-colors"
            >
              <div className="text-[11px] tracking-[0.2em] uppercase text-brand/70 mb-3">
                {o.category?.[lang] || o.category?.en}
              </div>
              <h3 className="font-display text-xl text-navy group-hover:text-brand leading-tight transition-colors">
                {(o[lang] || o.en).title}
              </h3>
              <div className="mt-6 flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-brand/80 group-hover:gap-3 transition-all">
                {t.common.readMore} <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
};

export default NewsArticle;
